import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import offerPageURL from 'lib/offer/offerPageURL'
import { escapeContextLabel } from 'tripPlanner/utils/contextTag'
import { buildOfferContext } from 'tripPlanner/utils/offerContext'

// ── Types ──

export interface MessageEnrichmentParams {
  message: string
  multiChoiceSelection: Array<string>
  pendingHotelOfferIds: string | undefined
  sendMessageOptions?: { offerId?: string; offerType?: string }
  mobileSelectionText: string | undefined
  offerContext: { propertyName: string; contextString: string } | undefined
}

// ── Offer helpers ──

export function getOfferPropertyName(offer: App.AnyOffer): string {
  if (isBedbankOffer(offer)) {
    return offer.property.name
  }
  if (isLEHotel(offer)) {
    return offer.property?.name || offer.name
  }
  return offer.name
}

export function extractRoomPackages(
  offer: App.AnyOffer,
  currentCurrency: string,
): Array<{ name: string; price?: number; currencyCode?: string }> {
  if (isBedbankOffer(offer)) {
    return offer.packages.slice(0, 3).map((pkg) => ({
      name: pkg.name || 'Room Package',
      price: pkg.sell?.price,
      currencyCode: currentCurrency,
    }))
  }

  if (isLEHotel(offer)) {
    return offer.packages.slice(0, 3).map((pkg) => ({
      name: pkg.roomType?.name || pkg.name || 'Room Package',
      price: pkg.price,
      currencyCode: currentCurrency,
    }))
  }

  return []
}

function getOfferDescription(offer: App.AnyOffer): string {
  if (isBedbankOffer(offer)) {
    return ('description' in offer && offer.description) || offer.tagline || ''
  }
  if (isLEHotel(offer)) {
    return offer.description || ''
  }
  return ''
}

function getOfferFinePrint(offer: App.AnyOffer): string | undefined {
  if (isLEHotel(offer)) {
    return offer.finePrint
  }
  if (isBedbankOffer(offer)) {
    return offer.property?.finePrintOverride
  }
  return undefined
}

export function buildOfferContextString(
  offer: App.AnyOffer,
  currentCurrency: string,
): { propertyName: string; contextString: string } {
  const propertyName = getOfferPropertyName(offer)
  const description = getOfferDescription(offer)
  const roomPackages = extractRoomPackages(offer, currentCurrency)
  const finePrint = getOfferFinePrint(offer)
  const offerUrl = offerPageURL(offer)

  const contextString = buildOfferContext(
    offer,
    propertyName,
    description,
    roomPackages,
    finePrint,
    offerUrl,
    currentCurrency,
  )

  return { propertyName, contextString }
}

// ── Message composition ──

export function buildEnrichedMessage(params: MessageEnrichmentParams): string {
  const {
    message,
    multiChoiceSelection,
    pendingHotelOfferIds,
    sendMessageOptions,
    mobileSelectionText,
    offerContext,
  } = params

  // Combine multi-choice selection with user's typed message
  const selectionPart = multiChoiceSelection.length > 0
    ? multiChoiceSelection.join(', ')
    : ''
  let combined = selectionPart && message.trim()
    ? `${selectionPart}, ${message}`
    : selectionPart || message

  // Append hotel information from carousel
  if (pendingHotelOfferIds) {
    combined = `${combined}\n\n<Context label="Hotel search results">${pendingHotelOfferIds}</Context>`
  }

  // Append LE offer ID when adding an offer to the trip
  if (sendMessageOptions?.offerId) {
    combined = `${combined}${buildOfferIdContext(sendMessageOptions.offerId, sendMessageOptions.offerType)}`
  }

  // Append mobile selection context
  if (mobileSelectionText) {
    combined = `${combined}\n<Context label="${escapeContextLabel(mobileSelectionText)}">The user highlighted the following text: ${mobileSelectionText}</Context>`
  }

  // Append offer context
  if (offerContext) {
    combined = `${combined}\n\n<Context label="${escapeContextLabel(offerContext.propertyName)}">${offerContext.contextString}</Context>`
  }

  return combined
}

export function buildOfferIdContext(offerId: string, offerType?: string): string {
  const offerTypeLine = offerType ? `\nOffer Type: ${offerType}` : ''
  const contextLabel = offerType === 'experience' ? 'LE Experience offer' : 'LE Hotel offer'
  return `\n<Context label="${contextLabel}">Offer ID: ${offerId}${offerTypeLine}</Context>`
}
